import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { geolocateApi } from '@/features/geolocate/api/geolocateApi'
import { toastService } from '@/lib/toastService'
import { HStack, Stack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { districtsBigApi } from '../api/streetsApi'

/**
 * District Big Modal Component
 * Form for creating/editing city districts
 */
export function DistrictBigModal({ isOpen, onClose, districtBig }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!districtBig

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      district_big: '',
      city: null,
    },
  })

  // Populate form when editing
  useEffect(() => {
    if (districtBig) {
      reset({
        district_big: districtBig.district_big || '',
        city: districtBig.city ? { id: districtBig.city_id, name: districtBig.city_name } : null,
      })
    } else {
      reset({ district_big: '', city: null })
    }
  }, [districtBig, reset])

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (formData) => {
      const data = {
        district_big: formData.district_big,
        city: formData.city?.id,
      }
      return isEdit ? districtsBigApi.update(districtBig.id, data) : districtsBigApi.create(data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['districts-big'] })
      toastService.success({
        title: t('common.success'),
        description: isEdit ? t('streets.districtBigUpdated') : t('streets.districtBigCreated'),
      })
      onClose()
    },
    onError: (error) => {
      toastService.error({
        title: t('common.error'),
        description: error?.message || t('streets.saveError'),
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      onClose()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? t('streets.editDistrictBig') : t('streets.createDistrictBig')}
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Fieldset>
            <Fieldset.Content>
              <Stack gap={4}>
                <Controller
                  name="district_big"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <Input
                      label={`${t('streets.districtBig')} *`}
                      error={errors.district_big?.message}
                      {...field}
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={control}
                  rules={{ required: t('validation.required') }}
                  render={({ field }) => (
                    <AutocompleteSelect
                      label={`${t('streets.city')} *`}
                      placeholder={t('streets.selectCity')}
                      fetchOptions={geolocateApi.getCitiesAutocomplete}
                      getOptionLabel={(option) => option.name}
                      getOptionValue={(option) => option.id}
                      error={errors.city?.message}
                      {...field}
                    />
                  )}
                />
              </Stack>
            </Fieldset.Content>
          </Fieldset>

          {/* Actions */}
          <HStack justify="flex-end" spacing={3} pt={2}>
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={handleClose}
              disabled={saveMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              size="md"
              loading={saveMutation.isPending}
            >
              {t('common.save')}
            </Button>
          </HStack>
        </Stack>
      </form>
    </Modal>
  )
}
